import type { CSSProperties } from 'react'

///////////
// BACKGROUND COLOR
///////////

export function getBuHistogramBarRankDarkRatio(pillIndex: number, pillCount: number): number {
  if (pillCount <= 1) {
    return 1
  }
  const clampedIndex = Math.min(Math.max(0, pillIndex), pillCount - 1)
  return (pillCount - 1 - clampedIndex) / (pillCount - 1)
}

export function getBuHistogramBarBackgroundColor(pillIndex: number, pillCount: number): string {
  const ratio = getBuHistogramBarRankDarkRatio(pillIndex, pillCount)
  return `color-mix(in srgb, var(--bu-histogram-bg-dark) ${ratio * 100}%, var(--bu-histogram-bg-light))`
}

/**
 * Sets `--bu-histogram-row-bg` for use with `background-color: var(--bu-histogram-row-bg, @primary-8)` in Less,
 * so `:hover` / `--selected` rules can still override `background-color` without fighting inline styles.
 */
export function getBuHistogramBarBackgroundStyle(pillIndex: number, pillCount: number): CSSProperties {
  return {
    '--bu-histogram-row-bg': getBuHistogramBarBackgroundColor(pillIndex, pillCount),
  } as CSSProperties
}

///////////
// WIDTH
///////////

/**
 * Width of a BU bar when `numberOfEmployees === 0` (% of the list column).
 * Bars with employees > 0 scale linearly from this value up to 100% vs the global max counter.
 */
export const BU_HISTOGRAM_ZERO_WIDTH_PERCENT = 50

/**
 * Width only for BU histogram rows.
 * - 100% width = BU with the maximum `numberOfEmployees` in the list.
 * - Counter 0 → fixed {@link BU_HISTOGRAM_ZERO_WIDTH_PERCENT}%.
 * - Counter n > 0 → `50% + (n / max) * 50%` (e.g. max 100 → 50→75%, 20→60%, 30→65%).
 */
export function getBuHistogramBarWidthStyle(numberOfEmployees: number, getBiggerBULength: number): CSSProperties {
  const maxCount = Math.max(1, getBiggerBULength)

  const widthPercent =
    numberOfEmployees <= 0
      ? BU_HISTOGRAM_ZERO_WIDTH_PERCENT
      : BU_HISTOGRAM_ZERO_WIDTH_PERCENT + (numberOfEmployees / maxCount) * (100 - BU_HISTOGRAM_ZERO_WIDTH_PERCENT)

  return {
    width: `min(100%, ${widthPercent}%)`,
    maxWidth: '100%',
    alignSelf: 'flex-start',
  }
}

export function getBiggerBULength(counts: readonly number[]): number {
  return counts.reduce((m, n) => Math.max(m, n), 0)
}
